import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';

import SahamDrawer from 'components/SahamDrawer';
import {
  TextField,
  Select,
  Typography,
  FormControlLabel,
  FormControl,
  MenuItem,
  InputLabel,
  RadioGroup,
  Radio,
  FormHelperText,
} from '@material-ui/core';
import { isEmpty, get } from 'lodash';
import messages from 'containers/PreOpeningPage/messages';
import { translate, arrayTranslate } from 'containers/services/translate';
import styled from 'styled-components';
import { DRIVER, EMPTY_DRIVER } from 'containers/data-structure';
import { CheckCircle } from '@material-ui/icons';
import { CHOICE } from 'containers/PreOpeningPage/constants';
import { SEX } from 'containers/RecapManagerPage/constants';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { Listings } from 'containers/AppContainer/typings';
import moment from 'moment-timezone';
import frLocale from 'date-fns/locale/fr';
import {
  displayErrorText,
  formsAreValidated,
} from 'containers/services/validateForm';
import { claimDeclarationValidator } from 'containers/PreOpeningPage/ClaimDeclaration';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import recapManagerMessages from 'containers/RecapManagerPage/messages';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';

export interface ClaimDeclarationDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: Function;
  listings?: Listings;
  data: { driver: any };
  setClaimRecap?: Function;
  closeClaimDeclarationDrawer: Function;
  claimDriverToUpdate: any;
  updateClaimDriver: Function;
  setDataClaimToUpdate?: Function;
  dataClaimToUpdate?: any;
}

/* istanbul ignore next */
const Container = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  .text {
    font-size: 13px;
    color: #374065;
    letter-spacing: 0px;
    font-weight: Medium;
    font-family: Roboto;
  }
  .MuiFormControlLabel-label {
    font-size: 13px;
    letter-spacing: -0.01px;
    color: #363f66;
  }
  .checkdiv {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .input {
    margin-bottom: 20px;
    font-size: 13px;
  }
  .radioGroup {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  .MuiFormControlLabel-root {
    margin-right: 5px;
  }
  .error {
    margin-top: -5px;
    color: red;
    text-align: center;
  }
  .errorSelect {
    margin-top: 9px;
    color: red;
  }
`;

const ClaimDeclarationDrawer: React.FC<ClaimDeclarationDrawerProps> = (
  props,
): JSX.Element => {
  /* istanbul ignore next */
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  /* istanbul ignore next */
  const [data, setData] = useState<any>({});
  const [
    claimDeclarationValidation,
    setClaimDeclarationValidation,
  ] = React.useState({});

  React.useEffect(() => {
    const expectedData = { ...props.data };
    const expectedDriver = get(expectedData, 'driver', {}) || {};
    const isDriverTheInsured = isEmpty(expectedDriver) ? CHOICE.YES : CHOICE.NO;
    const isDriverTheInsuredValue = get(
      expectedDriver,
      [DRIVER.IS_DRIVER_THE_INSURED],
      isDriverTheInsured,
    );
    expectedDriver[DRIVER.IS_DRIVER_THE_INSURED] = isDriverTheInsuredValue;
    setData({ ...expectedData, driver: expectedDriver });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* istanbul ignore next */
  const handleChange = (field: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value: any;
    }>,
  ) => {
    const { value } = event.target;
    let { driver, claim } = data;

    switch (field) {
      case DRIVER.IS_DRIVER_THE_INSURED:
        const val = !!(value === CHOICE.YES);

        driver = !val
          ? isEmpty(data.driver)
            ? { ...EMPTY_DRIVER, [DRIVER.IS_DRIVER_THE_INSURED]: CHOICE.NO }
            : { ...data.driver, [DRIVER.IS_DRIVER_THE_INSURED]: CHOICE.NO }
          : {
              ...props.data.driver,
              [DRIVER.IS_DRIVER_THE_INSURED]: CHOICE.YES,
            };

        break;
      case DRIVER.LAST_NAME:
        driver[DRIVER.LAST_NAME] = value;
        break;
      case DRIVER.FIRST_NAME:
        driver[DRIVER.FIRST_NAME] = value;
        break;
      case DRIVER.DATE_OF_BIRTH:
        driver[DRIVER.DATE_OF_BIRTH] = value;
        break;
      case DRIVER.CIN:
        driver[DRIVER.CIN] = value;
        break;
      case DRIVER.SEX:
        driver[DRIVER.SEX] = value;
        break;
      case DRIVER.LICENSE_START_DATE:
        driver[DRIVER.LICENSE_START_DATE] = value;
        break;
      case DRIVER.LICENSE_CATEGORY:
        driver[DRIVER.LICENSE_CATEGORY] = value;
        break;
      case DRIVER.LICENSE_ID:
        driver[DRIVER.LICENSE_ID] = value;
        break;
      case DRIVER.DRIVER_QUALITY:
        driver[DRIVER.DRIVER_QUALITY] = value;
        break;
      default:
        return driver;
    }
    setData({ ...data, driver, claim });
  };

  /* istanbul ignore next */
  const handleDateChange = (field: string) => (date: Date | null) => {
    let driver = get(data, 'driver', {});
    const theDate = getDateIgnoreTimezone(date);
    switch (field) {
      case DRIVER.DATE_OF_BIRTH:
        driver[DRIVER.DATE_OF_BIRTH] = moment(theDate as Date).format();
        break;
      case DRIVER.LICENSE_START_DATE:
        driver[DRIVER.LICENSE_START_DATE] = moment(theDate as Date).format();
        break;

      default:
        return driver;
    }

    setData({ ...data, driver });
  };

  const { driver } = data;

  const handleValidation = () => {
    const expectedData = { ...data };
    const expectedDriver = get(expectedData, 'driver', {}) || {};
    const isDriverTheInsuredValue = get(expectedDriver, [
      DRIVER.IS_DRIVER_THE_INSURED,
    ]);
    if (isDriverTheInsuredValue === CHOICE.YES) {
      expectedData.driver = {};
    }
    const validator = claimDeclarationValidator(driver) || {};
    setClaimDeclarationValidation(validator);
    if (formsAreValidated(validator)) {
      props.setDataClaimToUpdate &&
        props.setDataClaimToUpdate({
          ...props.dataClaimToUpdate,
          driver: {
            ...driver,
            isDriverTheInsured: isDriverTheInsuredValue === CHOICE.YES,
          },
        });
      props.setClaimRecap && props.setClaimRecap({ ...expectedData });
      props.updateClaimDriver({
        ...get(expectedData, 'driver', {}),
      });
      props.closeClaimDeclarationDrawer();
    }
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    claimDeclarationValidation,
    translateArrayErrorMessages(claimDeclarationValidation, true),
  );

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={
        /* istanbul ignore next */ () => props.handleToggleDrawer(true)
      }
      title={translate({ ...messages.claimDeclarationClaimDeclare }, props)}
      handleValidateDrawer={handleValidation}
      confirmButtonTitle={translate(
        recapManagerMessages.drawerSaveFormButtonTitle,
        props,
      )}
    >
      <Container>
        <div className="checkdiv">
          <Typography className="text">
            {translate(
              { ...messages.claimDeclarationDriverPolicyholder },
              props,
            )}
          </Typography>
          <FormControl>
            <RadioGroup
              row
              className="radioGroup"
              value={get(driver, [DRIVER.IS_DRIVER_THE_INSURED])}
              onChange={handleChange(DRIVER.IS_DRIVER_THE_INSURED)}
            >
              <FormControlLabel
                className="text-check"
                control={
                  <Radio
                    value={CHOICE.YES}
                    color="primary"
                    checkedIcon={<CheckCircle />}
                  />
                }
                label={translate({ ...messages.claimDeclarationYes }, props)}
              />
              <FormControlLabel
                className="text-check"
                control={
                  <Radio
                    color="primary"
                    value={CHOICE.NO}
                    checkedIcon={<CheckCircle />}
                  />
                }
                label={translate({ ...messages.claimDeclarationNo }, props)}
              />
            </RadioGroup>
            {/* istanbul ignore next */
            !!get(claimDeclarationValidation, [
              DRIVER.IS_DRIVER_THE_INSURED,
            ]) && (
              <FormHelperText className="error">
                {message[DRIVER.IS_DRIVER_THE_INSURED]}
              </FormHelperText>
            )}
          </FormControl>
        </div>
        {/* istanbul ignore next */
        get(driver, [DRIVER.IS_DRIVER_THE_INSURED], '') === CHOICE.NO && (
          <>
            <TextField
              id="standard-basic-n"
              name={DRIVER.LAST_NAME}
              value={get(driver, [DRIVER.LAST_NAME])}
              onChange={handleChange(DRIVER.LAST_NAME)}
              label={translate({ ...messages.claimDeclarationLastName }, props)}
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.LAST_NAME])}
              helperText={message[DRIVER.LAST_NAME]}
            ></TextField>
            <TextField
              id="standard-basic-v"
              name={DRIVER.FIRST_NAME}
              value={get(driver, [DRIVER.FIRST_NAME])}
              onChange={handleChange(DRIVER.FIRST_NAME)}
              label={translate(
                { ...messages.claimDeclarationFirstName },
                props,
              )}
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.FIRST_NAME])}
              helperText={message[DRIVER.FIRST_NAME]}
            ></TextField>
            <TextField
              id="standard-basic-o"
              name={DRIVER.CIN}
              value={get(driver, [DRIVER.CIN])}
              onChange={handleChange(DRIVER.CIN)}
              label={translate(
                { ...messages.claimDeclarationCINNumber },
                props,
              )}
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.CIN])}
              helperText={message[DRIVER.CIN]}
            ></TextField>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                invalidDateMessage={
                  !!get(claimDeclarationValidation, [DRIVER.DATE_OF_BIRTH])
                    ? message[DRIVER.DATE_OF_BIRTH]
                    : ''
                }
                format="dd/MM/yyyy"
                id="date-picker-inline-{}-date-of-birth"
                name={DRIVER.DATE_OF_BIRTH}
                value={dateStringToLocalDate(
                  get(driver, [DRIVER.DATE_OF_BIRTH], ''),
                )}
                onChange={handleDateChange(DRIVER.DATE_OF_BIRTH)}
                label={translate(
                  { ...messages.claimDeclarationBirthDate },
                  props,
                )}
                error={
                  get(driver, [DRIVER.DATE_OF_BIRTH], '') === 'Invalid date'
                }
                helperText={
                  get(driver, [DRIVER.DATE_OF_BIRTH], '') === 'Invalid date' &&
                  translate(messages.invalidDate, props)
                }
                className="input"
              />
            </MuiPickersUtilsProvider>
            <FormControl
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.SEX])}
            >
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate({ ...messages.claimDeclarationGender }, props)}
              </InputLabel>
              <Select
                name={DRIVER.SEX}
                value={get(driver, [DRIVER.SEX], '')}
                onChange={handleChange(DRIVER.SEX)}
                labelWidth={60}
              >
                <MenuItem value={SEX.MAN}>HOMME</MenuItem>
                <MenuItem value={SEX.WOMAN}>FEMME</MenuItem>
              </Select>
              {!!get(claimDeclarationValidation, [DRIVER.SEX]) && (
                <FormHelperText className="errorSelect">
                  {message[DRIVER.SEX]}
                </FormHelperText>
              )}
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                variant="inline"
                autoOk
                invalidDateMessage={
                  !!get(claimDeclarationValidation, [DRIVER.LICENSE_START_DATE])
                    ? message[DRIVER.LICENSE_START_DATE]
                    : get(driver, [DRIVER.LICENSE_START_DATE], '') ===
                      'Invalid date'
                    ? translate(messages.invalidDate, props)
                    : ''
                }
                format="dd/MM/yyyy"
                id="date-picker-inline-{}-license-start-date"
                name={DRIVER.LICENSE_START_DATE}
                value={dateStringToLocalDate(
                  get(driver, [DRIVER.LICENSE_START_DATE], ''),
                )}
                onChange={handleDateChange(DRIVER.LICENSE_START_DATE)}
                label={translate(
                  { ...messages.claimDeclarationLicenseDate },
                  props,
                )}
                className="input"
              />
            </MuiPickersUtilsProvider>
            <FormControl
              className="input"
              error={
                !!get(claimDeclarationValidation, [DRIVER.LICENSE_CATEGORY])
              }
            >
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate(
                  { ...messages.claimDeclarationLicenseCategory },
                  props,
                )}
              </InputLabel>
              <Select
                name={DRIVER.LICENSE_CATEGORY}
                value={get(driver, [DRIVER.LICENSE_CATEGORY], '')}
                onChange={handleChange(DRIVER.LICENSE_CATEGORY)}
                labelWidth={150}
              >
                {/* istanbul ignore next */
                !isEmpty(get(props.listings, 'drivingLicenseCategories', [])) &&
                  props.listings!.drivingLicenseCategories.map(category => (
                    <MenuItem value={category.id} key={category.id}>
                      {category.title}
                    </MenuItem>
                  ))}
              </Select>
              {!!get(claimDeclarationValidation, [DRIVER.LICENSE_CATEGORY]) && (
                <FormHelperText className="errorSelect">
                  {message[DRIVER.LICENSE_CATEGORY]}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              id="standard-basic-zz"
              name={DRIVER.LICENSE_ID}
              value={get(driver, [DRIVER.LICENSE_ID])}
              onChange={handleChange(DRIVER.LICENSE_ID)}
              label={translate(
                { ...messages.claimDeclarationLicenseNumber },
                props,
              )}
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.LICENSE_ID])}
              helperText={message[DRIVER.LICENSE_ID]}
            />
            <FormControl
              className="input"
              error={!!get(claimDeclarationValidation, [DRIVER.DRIVER_QUALITY])}
            >
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate({ ...messages.driverQuality }, props)}
              </InputLabel>
              <Select
                name={DRIVER.DRIVER_QUALITY}
                value={get(driver, [DRIVER.DRIVER_QUALITY], '')}
                onChange={handleChange(DRIVER.DRIVER_QUALITY)}
                labelWidth={150}
              >
                {!isEmpty(get(props.listings, 'driverQuality', [])) &&
                  props.listings!.driverQuality.map(quality => (
                    <MenuItem value={quality.id} key={quality.id}>
                      {quality.title}
                    </MenuItem>
                  ))}
              </Select>
              {!!get(claimDeclarationValidation, [DRIVER.DRIVER_QUALITY]) && (
                <FormHelperText className="errorSelect">
                  {message[DRIVER.DRIVER_QUALITY]}
                </FormHelperText>
              )}
            </FormControl>
          </>
        )}
      </Container>
    </SahamDrawer>
  );
};

export default injectIntl(ClaimDeclarationDrawer);
